import { Flex, Text, hooks } from '@bitrise/bitkit';
import React, { FunctionComponent, useContext, useEffect } from 'react';

import dataAtIds from '../../dataAtIds.json';
import useOverviewData from './useOverviewData';
import useFeatures from '../Features/useFeatures';
import { trackEvent } from '../../utils/tracking';
import Page from '../Page/Page';
import PageBody from '../Page/PageBody';
import PageHeader from '../Page/PageHeader';
import PageTitle from '../Page/PageTitle';
import { URLStateContext } from '../URLState/URLState';
import OverviewCrashFreeSessions from './OverviewCrashFreeSessions/OverviewCrashFreeSessions';
import OverviewIntro from './OverviewIntro';
import OverviewLatency from './OverviewLatency/OverviewLatency';
import OverviewLiveUsers from './OverviewLiveUsers/OverviewLiveUsers';
import OverviewSessionDuration from './OverviewSessionDuration/OverviewSessionDuration';
import OverviewTimeRangeTabs from './OverviewTimeRangeTabs';
import OverviewUserCounts from './OverviewUserCounts/OverviewUserCounts';
import OverviewUserEvents from './OverviewUserEvents/OverviewUserEvents';
import OverviewVersionAdoption from './OverviewVersionAdoption/OverviewVersionAdoption';
import getOverviewLabels from './getOverviewLabels';

const Overview: FunctionComponent = () => {
  const match = hooks.useMediaQuery(['564px', '1200px']);
  const { overviewPeriod } = useContext(URLStateContext);
  const { hasFeature } = useFeatures();
  const { metrics, duration, start, step, isError, isFetching, refetch } = useOverviewData(
    overviewPeriod,
  );

  const { range, ticks } = getOverviewLabels({
    duration,
    start,
    step,
    period: overviewPeriod,
  });

  /**
   * Use a side-effect to:
   *  - Track changes to the selected OverviewPeriod
   */
  useEffect(() => {
    trackEvent('Overview.Visit', { period: overviewPeriod });
  }, [overviewPeriod]);

  return (
    <Page data-trace-at-id={dataAtIds.OverviewPage}>
      <PageHeader>
        <Flex
          alignChildren="middle"
          direction={match('564px') ? 'horizontal' : 'vertical'}
          gap="x6"
          margin="x10"
        >
          <Flex grow initial="none">
            <PageTitle>Overview</PageTitle>
            <Text config="5" margin="x5">
              {range || ' '}
            </Text>
          </Flex>

          <Flex>
            <OverviewTimeRangeTabs />
          </Flex>
        </Flex>

        <Flex
          direction={match('1200px') ? 'horizontal' : 'vertical'}
          alignChildrenHorizontal="between"
          alignChildrenVertical="start"
          margin="x12"
          gap={match('1200px') ? 'x16' : 'x4'}
        >
          <Flex grow="x2" initial="none">
            <OverviewIntro />
          </Flex>

          <Flex grow initial="none">
            <OverviewLiveUsers />
          </Flex>
        </Flex>

        <Flex
          direction={match('1200px') ? 'horizontal' : 'vertical'}
          gap={match('1200px') ? 'x16' : 'x5'}
          margin="x10"
        >
          <Flex grow="x2" initial="none">
            <OverviewUserCounts
              dataActive={metrics?.activeUsers}
              dataNew={metrics?.newUsers}
              isError={isError}
              isFetching={isFetching}
              refetch={refetch}
              xAxisLabels={ticks}
            />
          </Flex>

          <Flex grow initial="none">
            <OverviewUserEvents
              data={metrics?.userEvents}
              isError={isError}
              isFetching={isFetching}
              refetch={refetch}
              xAxisLabels={ticks}
            />
          </Flex>
        </Flex>
      </PageHeader>

      <PageBody>
        <Flex>
          <OverviewCrashFreeSessions
            data={metrics?.crashFreeSessions}
            isError={isError}
            isFetching={isFetching}
            refetch={refetch}
            xAxisLabels={ticks}
          />

          <Flex direction={match('1200px') ? 'horizontal' : 'vertical'} gap="x8" margin="x8">
            <Flex direction="vertical" grow initial={match('1200px') ? 'none' : undefined}>
              {hasFeature('enable-overview-cold-startup-latency') ? (
                <OverviewLatency
                  data={metrics?.coldStartupLatency}
                  isError={isError}
                  isFetching={isFetching}
                  refetch={refetch}
                  title="Cold Startup"
                  xAxisLabels={ticks}
                />
              ) : (
                <OverviewLatency
                  data={metrics?.networkLatency}
                  isError={isError}
                  isFetching={isFetching}
                  refetch={refetch}
                  title="Network Latency"
                  xAxisLabels={ticks}
                />
              )}
            </Flex>

            <Flex direction="vertical" grow initial={match('1200px') ? 'none' : undefined}>
              <OverviewVersionAdoption
                data={metrics?.versionAdoption}
                isError={isError}
                isFetching={isFetching}
                refetch={refetch}
                xAxisLabels={ticks}
              />
            </Flex>
          </Flex>

          <OverviewSessionDuration
            dataAvg={metrics?.sessionDurationAvg}
            dataMax={metrics?.sessionDurationMax}
            dataMin={metrics?.sessionDurationMin}
            isError={isError}
            isFetching={isFetching}
            refetch={refetch}
            xAxisLabels={ticks}
          />
        </Flex>
      </PageBody>
    </Page>
  );
};

export default Overview;
